import {
    Engine,
    Actor,
    Color,
    vec,
    Vector,
    ScreenElement,
    Rectangle,
    Label,
    Font,
    TextAlign,
    CoordPlane,
    Animation,
} from "excalibur";
import { AudioManager } from "../systems/AudioManager";
import { Resources } from "../resources";
import { createPackedAnimation } from "../utils/AtlasLoader";

/**
 * SettingsPopup - Modal overlay for adjusting audio settings
 * 
 * Features sliders with perforin_zone animation as draggable knobs
 * for SFX and Music volume control.
 */
export class SettingsPopup {
    private engine: Engine;
    private isVisible: boolean = false;
    private actors: Actor[] = [];

    // UI Elements
    private overlay!: ScreenElement;
    private panel!: ScreenElement;
    private titleLabel!: Label;
    private sfxLabel!: Label;
    private musicLabel!: Label;
    private sfxSliderBg!: ScreenElement;
    private musicSliderBg!: ScreenElement;
    private sfxKnob!: Actor;
    private musicKnob!: Actor;
    private sfxValueLabel!: Label;
    private musicValueLabel!: Label;
    private closeButton!: Label;

    // Slider dimensions
    private sliderWidth: number = 200;
    private sliderHeight: number = 20;
    private knobSize: number = 40;

    // Knob animation
    private knobAnimation: Animation | null = null;

    // Dragging state
    private draggingSfx: boolean = false;
    private draggingMusic: boolean = false;

    constructor(engine: Engine) {
        this.engine = engine;
        this.createUI();
    }

    private createUI(): void {
        const w = this.engine.drawWidth;
        const h = this.engine.drawHeight;

        // Load knob animation
        try {
            this.knobAnimation = createPackedAnimation(
                Resources.PerforinZoneImage,
                Resources.PerforinZoneJson,
                8
            );
        } catch (e) {
            console.warn('Failed to load perforin_zone animation for settings knob');
        }

        // Semi-transparent overlay
        this.overlay = new ScreenElement({
            pos: vec(0, 0),
            anchor: Vector.Zero,
            z: 900,
        });
        this.overlay.graphics.use(new Rectangle({
            width: w,
            height: h,
            color: Color.fromRGB(0, 0, 0, 0.7),
        }));
        this.overlay.on('pointerdown', () => this.hide());
        this.actors.push(this.overlay);

        // Panel background
        const panelWidth = Math.min(400, w * 0.9);
        const panelHeight = Math.min(300, h * 0.6);
        this.panel = new ScreenElement({
            pos: vec(w / 2, h / 2),
            anchor: Vector.Half,
            z: 901,
        });
        this.panel.graphics.use(new Rectangle({
            width: panelWidth,
            height: panelHeight,
            color: Color.fromRGB(30, 30, 50, 0.95),
            strokeColor: Color.fromHex('#4ade80'),
            lineWidth: 3,
        }));
        // Prevent clicks on panel from closing
        this.panel.on('pointerdown', (evt) => evt.cancel());
        this.actors.push(this.panel);

        // Title
        this.titleLabel = new Label({
            text: 'SETTINGS',
            pos: vec(w / 2, h / 2 - panelHeight / 2 + 40),
            font: new Font({
                family: 'Courier New, monospace',
                size: 32,
                bold: true,
                color: Color.White,
                textAlign: TextAlign.Center,
            }),
            anchor: Vector.Half,
            z: 902,
        });
        this.titleLabel.transform.coordPlane = CoordPlane.Screen;
        this.actors.push(this.titleLabel);

        // SFX Volume
        const sfxY = h / 2 - 80;
        this.sfxLabel = new Label({
            text: 'SFX Volume',
            pos: vec(w / 2 - panelWidth / 2 + 25, sfxY),
            font: new Font({
                family: 'Courier New, monospace',
                size: 18,
                bold: true,
                color: Color.White,
                textAlign: TextAlign.Left,
            }),
            anchor: vec(0, 0.5),
            z: 902,
        });
        this.sfxLabel.transform.coordPlane = CoordPlane.Screen;
        this.actors.push(this.sfxLabel);

        // SFX slider background
        const sliderStartX = w / 2 - this.sliderWidth / 2 + 20;
        this.sfxSliderBg = new ScreenElement({
            pos: vec(sliderStartX, sfxY),
            anchor: vec(0, 0.5),
            z: 902,
        });
        this.sfxSliderBg.graphics.use(new Rectangle({
            width: this.sliderWidth,
            height: this.sliderHeight,
            color: Color.fromHex('#333333'),
            strokeColor: Color.fromHex('#666666'),
            lineWidth: 2,
        }));
        this.actors.push(this.sfxSliderBg);

        // SFX knob
        this.sfxKnob = new Actor({
            pos: vec(sliderStartX, sfxY),
            anchor: Vector.Half,
            z: 903,
        });
        this.sfxKnob.transform.coordPlane = CoordPlane.Screen;
        this.setupKnob(this.sfxKnob);
        this.sfxKnob.on('pointerdown', () => { this.draggingSfx = true; });
        this.actors.push(this.sfxKnob);

        // SFX value label
        this.sfxValueLabel = new Label({
            text: '100%',
            pos: vec(sliderStartX + this.sliderWidth + 50, sfxY),
            font: new Font({
                family: 'Courier New, monospace',
                size: 16,
                bold: true,
                color: Color.fromHex('#4ade80'),
                textAlign: TextAlign.Left,
            }),
            anchor: vec(0, 0.5),
            z: 902,
        });
        this.sfxValueLabel.transform.coordPlane = CoordPlane.Screen;
        this.actors.push(this.sfxValueLabel);

        // Music Volume
        const musicY = h / 2 + 35;
        this.musicLabel = new Label({
            text: 'Music Volume',
            pos: vec(w / 2 - panelWidth / 2 + 25, musicY),
            font: new Font({
                family: 'Courier New, monospace',
                size: 18,
                bold: true,
                color: Color.White,
                textAlign: TextAlign.Left,
            }),
            anchor: vec(0, 0.5),
            z: 902,
        });
        this.musicLabel.transform.coordPlane = CoordPlane.Screen;
        this.actors.push(this.musicLabel);

        // Music slider background
        this.musicSliderBg = new ScreenElement({
            pos: vec(sliderStartX, musicY),
            anchor: vec(0, 0.5),
            z: 902,
        });
        this.musicSliderBg.graphics.use(new Rectangle({
            width: this.sliderWidth,
            height: this.sliderHeight,
            color: Color.fromHex('#333333'),
            strokeColor: Color.fromHex('#666666'),
            lineWidth: 2,
        }));
        this.actors.push(this.musicSliderBg);

        // Music knob
        this.musicKnob = new Actor({
            pos: vec(sliderStartX, musicY),
            anchor: Vector.Half,
            z: 903,
        });
        this.musicKnob.transform.coordPlane = CoordPlane.Screen;
        this.setupKnob(this.musicKnob);
        this.musicKnob.on('pointerdown', () => { this.draggingMusic = true; });
        this.actors.push(this.musicKnob);

        // Music value label
        this.musicValueLabel = new Label({
            text: '50%',
            pos: vec(sliderStartX + this.sliderWidth + 50, musicY),
            font: new Font({
                family: 'Courier New, monospace',
                size: 16,
                bold: true,
                color: Color.fromHex('#4ade80'),
                textAlign: TextAlign.Left,
            }),
            anchor: vec(0, 0.5),
            z: 902,
        });
        this.musicValueLabel.transform.coordPlane = CoordPlane.Screen;
        this.actors.push(this.musicValueLabel);

        // Close button
        this.closeButton = new Label({
            text: '✕ CLOSE',
            pos: vec(w / 2, h / 2 + panelHeight / 2 - 40),
            font: new Font({
                family: 'Courier New, monospace',
                size: 20,
                bold: true,
                color: Color.fromHex('#ef4444'),
                textAlign: TextAlign.Center,
            }),
            anchor: Vector.Half,
            z: 902,
        });
        this.closeButton.transform.coordPlane = CoordPlane.Screen;
        this.closeButton.on('pointerdown', () => this.hide());
        this.closeButton.on('pointerenter', () => {
            this.closeButton.scale = vec(1.1, 1.1);
        });
        this.closeButton.on('pointerleave', () => {
            this.closeButton.scale = vec(1, 1);
        });
        this.closeButton.pointer.useGraphicsBounds = true;
        this.actors.push(this.closeButton);

        // Enable pointer events on knobs
        this.sfxKnob.pointer.useGraphicsBounds = true;
        this.musicKnob.pointer.useGraphicsBounds = true;

        // Global pointer move and up handlers
        this.engine.input.pointers.primary.on('move', (evt) => {
            if (!this.isVisible) return;

            const screenPos = evt.screenPos;
            const sliderLeft = this.sfxSliderBg.pos.x;
            const sliderRight = sliderLeft + this.sliderWidth;

            if (this.draggingSfx) {
                const x = Math.max(sliderLeft, Math.min(sliderRight, screenPos.x));
                this.sfxKnob.pos.x = x;
                const volume = (x - sliderLeft) / this.sliderWidth;
                AudioManager.setSfxVolume(volume);
                this.sfxValueLabel.text = `${Math.round(volume * 100)}%`;
            }

            if (this.draggingMusic) {
                const x = Math.max(sliderLeft, Math.min(sliderRight, screenPos.x));
                this.musicKnob.pos.x = x;
                const volume = (x - sliderLeft) / this.sliderWidth;
                AudioManager.setMusicVolume(volume);
                this.musicValueLabel.text = `${Math.round(volume * 100)}%`;
            }
        });

        this.engine.input.pointers.primary.on('up', () => {
            this.draggingSfx = false;
            this.draggingMusic = false;
        });
    }

    private setupKnob(knob: Actor): void {
        if (this.knobAnimation) {
            const anim = this.knobAnimation.clone();
            const scale = this.knobSize / anim.width;
            anim.scale = vec(scale, scale);
            knob.graphics.use(anim);
        } else {
            // Fallback to circle
            knob.graphics.use(new Rectangle({
                width: this.knobSize,
                height: this.knobSize,
                color: Color.fromHex('#4ade80'),
            }));
        }
    }

    /**
     * Show the settings popup
     */
    public show(): void {
        if (this.isVisible) return;
        this.isVisible = true;

        // Update layout for current screen size
        this.updateLayout();

        // Update knob positions based on current values
        const sliderLeft = this.sfxSliderBg.pos.x;
        this.sfxKnob.pos.x = sliderLeft + AudioManager.getSfxVolume() * this.sliderWidth;
        this.musicKnob.pos.x = sliderLeft + AudioManager.getMusicVolume() * this.sliderWidth;
        this.sfxValueLabel.text = `${Math.round(AudioManager.getSfxVolume() * 100)}%`;
        this.musicValueLabel.text = `${Math.round(AudioManager.getMusicVolume() * 100)}%`;

        // Add all actors to scene
        for (const actor of this.actors) {
            this.engine.add(actor);
        }
    }

    /**
     * Hide the settings popup
     */
    public hide(): void {
        if (!this.isVisible) return;
        this.isVisible = false;
        this.draggingSfx = false;
        this.draggingMusic = false;

        // Remove all actors from scene
        for (const actor of this.actors) {
            this.engine.remove(actor);
        }
    }

    /**
     * Toggle visibility
     */
    public toggle(): void {
        if (this.isVisible) {
            this.hide();
        } else {
            this.show();
        }
    }

    /**
     * Check if popup is visible
     */
    public getIsVisible(): boolean {
        return this.isVisible;
    }

    /**
     * Update layout for current screen size (responsive)
     */
    public updateLayout(): void {
        const w = this.engine.drawWidth;
        const h = this.engine.drawHeight;
        const isPortrait = h > w;

        // Adjust panel size for orientation
        const panelWidth = isPortrait ? Math.min(350, w * 0.95) : Math.min(450, w * 0.6);
        const panelHeight = isPortrait ? Math.min(280, h * 0.5) : Math.min(300, h * 0.7);

        // Update overlay
        this.overlay.graphics.use(new Rectangle({
            width: w,
            height: h,
            color: Color.fromRGB(0, 0, 0, 0.7),
        }));

        // Update panel
        this.panel.pos = vec(w / 2, h / 2);
        this.panel.graphics.use(new Rectangle({
            width: panelWidth,
            height: panelHeight,
            color: Color.fromRGB(30, 30, 50, 0.95),
            strokeColor: Color.fromHex('#4ade80'),
            lineWidth: 3,
        }));

        // Adjust slider width based on panel
        this.sliderWidth = panelWidth * 0.45;
        const sliderStartX = w / 2 - this.sliderWidth / 2 + 20;
        const labelX = w / 2 - panelWidth / 2 + 20;

        // Title
        this.titleLabel.pos = vec(w / 2, h / 2 - panelHeight / 2 + 35);

        // SFX row
        const sfxY = h / 2 - 35;
        this.sfxLabel.pos = vec(labelX - 5, sfxY - 10);
        this.sfxSliderBg.pos = vec(sliderStartX, sfxY);
        this.sfxSliderBg.graphics.use(new Rectangle({
            width: this.sliderWidth,
            height: this.sliderHeight,
            color: Color.fromHex('#333333'),
            strokeColor: Color.fromHex('#666666'),
            lineWidth: 2,
        }));
        this.sfxKnob.pos.y = sfxY;
        this.sfxValueLabel.pos = vec(sliderStartX + this.sliderWidth + 15, sfxY - 10);

        // Music row
        const musicY = h / 2 + 35;
        this.musicLabel.pos = vec(labelX - 10, musicY - 10);
        this.musicSliderBg.pos = vec(sliderStartX, musicY);
        this.musicSliderBg.graphics.use(new Rectangle({
            width: this.sliderWidth,
            height: this.sliderHeight,
            color: Color.fromHex('#333333'),
            strokeColor: Color.fromHex('#666666'),
            lineWidth: 2,
        }));
        this.musicKnob.pos.y = musicY;
        this.musicValueLabel.pos = vec(sliderStartX + this.sliderWidth + 15, musicY - 10);

        // Close button
        this.closeButton.pos = vec(w / 2, h / 2 + panelHeight / 2 - 35);

        // Update knob X positions based on current values
        this.sfxKnob.pos.x = sliderStartX + AudioManager.getSfxVolume() * this.sliderWidth;
        this.musicKnob.pos.x = sliderStartX + AudioManager.getMusicVolume() * this.sliderWidth;
    }
}
